import React, { useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { Button, Popover, Stack } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, usePrompt, useUrlHooks } from "hooks";
import { useReportsQueriesSelector, useSelectBookmarksAll, useUserActions, useUserIdSelector } from "store";
import { IBookmark, UrlQueryParams, UserLight } from "store/types";

import { AVAILABLE_ROLES_FOR_BOOKMARK, getBookmarkPage, getCurrentPageContext } from "./bookmark-page";
import { BookmarkPanel } from "./BookmarkPanel";
import { SaveViewPrompt } from "./SaveViewPrompt";
import { PanelStatus } from "./types";

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export const Bookmark: React.FC = () => {
  const { getBookmarksAction, getUsersByRoleAction } = useUserActions();
  const userId = useUserIdSelector();
  const bookmarks = useSelectBookmarksAll();
  const { pathname, search } = useLocation();
  const { queryParams } = useUrlHooks();
  const reportsQueries = useReportsQueriesSelector();
  const openPrompt = usePrompt();

  const { action: getUsersByRole } = useActionHook(() => getUsersByRoleAction(AVAILABLE_ROLES_FOR_BOOKMARK), {
    onSuccess: (users) => setOwnersById(new Map(users.map((user) => [user.id, user])))
  });

  const { action: getBookmarks } = useActionHook(
    () => {
      setStatus("loading");
      return getBookmarksAction();
    },
    {
      onSuccess: () => setStatus("ready"),
      onReject: () => setStatus("error")
    }
  );

  const containerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<PanelStatus>("ready");
  const [ownersById, setOwnersById] = useState<Map<number, UserLight>>(new Map());

  const currentPage = useMemo(() => getCurrentPageContext(pathname), [pathname]);

  const canSave = useMemo(() => {
    if (!currentPage) return false;
    if (currentPage.type === "data_operations") return !!search;
    if (currentPage.type === "report") return !!reportsQueries && Boolean(Object.keys(reportsQueries).length);
    return !!queryParams[currentPage.target as keyof UrlQueryParams];
  }, [currentPage, search, reportsQueries, queryParams]);

  const sortedBookmarks = useMemo(() => [...bookmarks].sort((a, b) => collator.compare(a.name, b.name)), [bookmarks]);

  const { ownedByPage, sharedWithMe } = useMemo(() => {
    const owned: Record<string, { label: string; key: string; items: IBookmark[] }> = {};
    const shared: IBookmark[] = [];

    sortedBookmarks.forEach((bookmark) => {
      if (bookmark.user_id !== userId) {
        shared.push(bookmark);
        return;
      }
      const page = getBookmarkPage(bookmark);
      if (!owned[page.key]) {
        owned[page.key] = { label: page.label, key: page.key, items: [] };
      }
      owned[page.key].items.push(bookmark);
    });

    return { ownedByPage: owned, sharedWithMe: shared };
  }, [sortedBookmarks, userId]);

  const orderedOwnedGroups = useMemo(() => {
    const groups = Object.values(ownedByPage);
    if (!currentPage) return groups;
    return groups.sort((a, b) => {
      if (a.key === currentPage.pageKey) return -1;
      if (b.key === currentPage.pageKey) return 1;
      return collator.compare(a.label, b.label);
    });
  }, [ownedByPage, currentPage]);

  const handleOpen = useCallback(async () => {
    setIsOpen(true);
    await getBookmarks();
    await getUsersByRole();
  }, [getBookmarks, getUsersByRole]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSaveClick = useCallback(() => {
    setIsOpen(false);
    openPrompt({
      Component: SaveViewPrompt,
      componentType: "modal",
      props: { currentPage, currentPathname: pathname }
    });
  }, [openPrompt, currentPage, pathname]);

  return (
    <>
      <MaterialTooltip
        isShow={!isOpen}
        title="Manage Bookmarks: view saved pages or add the current page"
        placement="bottom"
        variant="dark"
      >
        <Button
          ref={containerRef}
          onClick={handleOpen}
          disableRipple
          sx={{
            minWidth: 0,
            padding: "4px 16px",
            borderRadius: "32px",
            color: "#071f35",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "24px",
            textTransform: "none",
            backgroundColor: isOpen ? "rgba(7,31,53,0.10)" : "transparent",
            "&:hover": { backgroundColor: "rgba(7,31,53,0.10)" },
            display: "flex",
            alignItems: "center",
            "& .MuiButton-startIcon": { marginRight: "4px", marginLeft: 0 }
          }}
          startIcon={<BookmarkBorderIcon sx={{ fontSize: "20px !important" }} />}
        >
          Saved views
        </Button>
      </MaterialTooltip>

      <Popover
        open={isOpen}
        onClose={handleClose}
        anchorEl={containerRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: "8px",
              width: "380px",
              maxHeight: "600px",
              borderRadius: "8px",
              border: "1px solid #dbe8f5",
              boxShadow:
                "0px 3px 3px -2px rgba(38,54,70,.16), 0px 3px 4px 0px rgba(38,54,70,.10), 0px 1px 8px 0px rgba(38,54,70,.08)",
              overflow: "hidden"
            }
          }
        }}
      >
        <Stack sx={{ maxHeight: "600px" }}>
          <BookmarkPanel
            status={status}
            ownedGroups={orderedOwnedGroups}
            sharedWithMe={sharedWithMe}
            ownersById={ownersById}
            currentPage={currentPage}
            canSave={canSave}
            onClose={handleClose}
            onRetry={getBookmarks}
            onSaveClick={handleSaveClick}
          />
        </Stack>
      </Popover>
    </>
  );
};
